import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment as env } from '../../../../../../environments/environment';
import {
  EvaluateRouteRequest,
  EvaluateRouteResponse,
  PlanMultidayRequest,
  PlanMultidayResponse,
  PlanRequest,
  PlanResponse,
  RouteMapRequest,
  RouteMapResponse,
  SortRequest,
  SortResponse,
} from './route-optimization.interface';

const apiUrl = env.API_ROUTE_OPTIMIZATION_URL;

/**
 * Cliente HTTP de la route-optimization API (FastAPI/VROOM).
 *
 * NO extiende `AbstractService`: la route-api no sigue la convención REST de AVE
 * (`byId`/`filter`/`IRequestResult<T>`), son endpoints POST con shape propio.
 * Los errores HTTP (`422` entrada inválida, `503` motor no disponible) se
 * propagan tal cual para que el consumidor decida el mensaje al usuario.
 */
@Injectable({
  providedIn: 'root',
})
export class RouteOptimizationService {
  /**
   * Header que le pide al `fuseLoadingInterceptor` NO mostrar el overlay global
   * de carga: las dos pantallas que optimizan tienen su propia animación.
   *
   * El valor tiene que ser EXACTAMENTE uno de los que el interceptor reconoce
   * (`export` o `skip-loading`, ver `@fuse/services/loading/loading.interceptor`).
   * Decía `no-loader`, que no está en esa lista, así que el header viajaba y no
   * hacía nada: cada una de las 17+ peticiones de una corrida encendía el overlay
   * de AVE a pantalla completa encima del modal. Un valor equivocado acá no da
   * error —el interceptor simplemente no lo reconoce—, y el docstring afirmaba lo
   * contrario de lo que pasaba.
   */
  private readonly silentLoading = { 'x-ave-ctx': 'skip-loading' };

  constructor(private readonly http: HttpClient) {}

  /**
   * `POST /sort` — devuelve el orden óptimo de visita (TSP, un recorrido).
   * @param body paradas (mínimo 2) y, opcionalmente, el punto de partida.
   */
  sort(body: SortRequest): Observable<SortResponse> {
    return this.http.post<SortResponse>(`${apiUrl}sort`, body, { headers: this.silentLoading });
  }

  /**
   * `POST /plan-multiday` — planifica un horizonte de días: decide el día de
   * cada visita (según su elegibilidad `delivery_days`) y ordena cada día.
   */
  planMultiday(body: PlanMultidayRequest): Observable<PlanMultidayResponse> {
    return this.http.post<PlanMultidayResponse>(`${apiUrl}plan-multiday`, body, {
      headers: this.silentLoading,
    });
  }

  /**
   * `POST /plan` — asigna las órdenes de un día a una flota (modo `trucks` o
   * `fleet`) respetando capacidad; devuelve una ruta por camión y las no
   * asignadas.
   */
  plan(body: PlanRequest): Observable<PlanResponse> {
    return this.http.post<PlanResponse>(`${apiUrl}plan`, body, { headers: this.silentLoading });
  }

  /**
   * `POST /route-map` — el mismo plan de `/plan` **más** su `geojson` listo para
   * pintar: una `LineString` por ruta (polyline ya decodificado en el servidor)
   * y un `Point` por parada y por almacén.
   *
   * Sustituye a `plan()` cuando la pantalla dibuja el recorrido: es el mismo
   * optimizador, así que no agrega llamadas al motor ni latencia apreciable
   * (medido: 2,72 s vs 2,78 s), solo el paso de geometría y ~5× de payload.
   */
  /**
   * `POST /evaluate-route` — tiempo y distancia de la secuencia EN EL ORDEN DADO,
   * sin optimizar. Es la mitad baseline de "cuánto ahorra el optimizador": el
   * backend la calcula desde la misma matriz de Valhalla que alimenta al plan,
   * así que la resta es manzanas con manzanas por construcción.
   */
  evaluateRoute(body: EvaluateRouteRequest): Observable<EvaluateRouteResponse> {
    return this.http.post<EvaluateRouteResponse>(`${apiUrl}evaluate-route`, body, {
      headers: this.silentLoading,
    });
  }

  routeMap(body: RouteMapRequest): Observable<RouteMapResponse> {
    return this.http.post<RouteMapResponse>(`${apiUrl}route-map`, body, {
      headers: this.silentLoading,
    });
  }
}
